<template>
  <div
    ref="popover"
    class="lp-calendar-popout"
    :style="{ '--popout-column-definition': `repeat(${columnCount}, 1fr)` }"
    @mousedown.prevent
  >
    <div class="lp-calendar-heading pt-1">
      <component :is="'div'" class="lp-calendar-heading-center">
        <slot name="heading" />
      </component>
    </div>
    <div class="lp-calendar-body">
      <template v-if="'subheading' in $slots">
        <div class="lp-calendar-subheading">
          <slot name="subheading" />
        </div>
        <hr class="lp-calendar-divider" />
      </template>
      <div class="lp-calendar-elements">
        <slot name="body">
          <button
            v-for="item in items"
            :key="item.key"
            :disabled="item.disabled"
            :class="{ selected: item.selected }"
            data-ua-multiple-id="ua-datepicker-option"
            :data-ua-value="item.display"
            :aria-label="
              item.value.toLocaleDateString('en-us', {
                weekday: 'long',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            "
            @click.stop.prevent="$emit('elementClick', item.value)"
          >
            {{ item.display }}
            <div
              v-if="hasHighlight(item.value)"
              class="notification-dot-container datepicker-notifications"
            >
              <div
                v-for="highlight in highlights"
                :key="highlight.date"
                :style="`${
                  isSameDay(highlight.date, item.value) ? '' : 'display: none;'
                }`"
                class="notification-dot-sm"
                :class="`bg-${highlight.color}`"
              />
            </div>
          </button>
        </slot>
      </div>
    </div>
    <div v-if="highlights.length" class="lp-highlight-datepicker-tags p-1">
      <span
        v-for="highlight in highlights"
        :key="highlight.date"
        class="tag"
        :class="`tag-${highlight.color}`"
      >
        {{ highlight.label }}
      </span>
    </div>
    <lp-icon name="" />
  </div>
</template>

<script lang="ts">
import { isValid, isSameDay } from 'date-fns'
import { defineComponent, PropType, ref } from 'vue'
import { usePopover } from '@/hooks'
import { Positions } from '@/models/enums'

type Item = {
  key: string
  value: Date
  display: number | string
  disabled: boolean
  selected: boolean
}
type Highlight = {
  label: string
  date: Date
  color: string
}

export default defineComponent({
  props: {
    headingClickable: {
      type: Boolean,
      default: false,
    },
    leftDisabled: {
      type: Boolean,
      default: false,
    },
    rightDisabled: {
      type: Boolean,
      default: false,
    },
    columnCount: {
      type: Number,
      default: 7,
    },
    items: {
      type: Array as PropType<Item[]>,
      default: (): Item[] => [],
    },
    highlights: {
      type: Array as PropType<Highlight[]>,
      default: (): Highlight[] => [],
    },
    placement: {
      type: String,
      default: Positions.T,
      validator: (value: Positions) => Object.values(Positions).includes(value),
    },
    permanent: Boolean,
    autoposition: { type: Boolean, default: true },
  },
  emits: {
    elementClick: (value: Date) => isValid(value),
    left: () => true,
    right: () => true,
    heading: () => true,
  },
  setup(props) {
    const popover = ref(null)
    const trigger = ref(null)

    const {
      show,
      placement,
      disabled,
      clickable,
      transition,
      visionTimer,
      position,
      handleMouseEnter,
      handleMouseLeave,
      hidePopover,
      showPopover,
      setPopoverPosition,
    } = usePopover({
      ...props,
      popover,
      trigger,
      arrowCalculation: false,
    })

    const hasHighlight = (pickerDate: Date) => {
      return props.highlights.find((highlight) =>
        isSameDay(highlight.date, pickerDate),
      )
    }

    return {
      show,
      placement,
      disabled,
      clickable,
      transition,
      visionTimer,
      popover,
      trigger,
      position,
      handleMouseEnter,
      handleMouseLeave,
      hidePopover,
      showPopover,
      setPopoverPosition,
      isSameDay,
      hasHighlight,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '../../scss/_variables.scss';

.lp-calendar {
  position: relative;

  &-preview {
    display: inline;
  }
}

.lp-calendar-popout {
  z-index: $z-index-popover;
  text-align: center;
  border-top: 1px solid var(--black-20);
  border-left: 1px solid var(--black-20);
  background: var(--white);
  margin-top: 0.5rem;

  &:nth-child(4n) {
    border-right: 1px solid var(--black-20);
  }
  &:nth-child(n + 9) {
    border-bottom: 1px solid var(--black-20);
  }

  button + button,
  .btn + .btn {
    margin-left: unset;
  }
}

.lp-calendar-popout * {
  font-weight: inherit;
}

.lp-calendar-popout button {
  background: none;
  border: none;
  outline: none;
}

.lp-calendar-popout button:not(:disabled) {
  cursor: pointer;
}

.lp-calendar-heading,
.lp-calendar-body {
  padding: 0.5rem;
}

.lp-calendar-heading {
  width: 100%;
  display: flex;

  &-center {
    flex: 1;

    span {
      margin-left: 0.5rem;
    }
  }
}

.lp-calendar-subheading,
.lp-calendar-elements {
  display: grid;
  grid-template-columns: var(--popout-column-definition);
  gap: 0.5rem 0.3rem;
  justify-items: center;
  button {
    height: 2rem;
    width: 2rem;
    position: relative;
    margin-left: 1rem;
  }
}

.lp-calendar-subheading {
  text-align: center;
  font-size: 0.75rem;
  text-transform: uppercase;
  margin-top: 1em;
  color: var(--black-30);
}

.lp-calendar-divider {
  border: 1px solid var(--divider-color);
  border-radius: $border-radius-base;
}

.lp-calendar-elements button:disabled {
  border: 0;
}

.lp-calendar-elements button {
  color: unset;
  font-size: var(--font-size-xs);
  border-radius: $border-radius-full;
}

.lp .lp-calendar-heading button,
.lp .lp-calendar-heading button:hover {
  color: unset;
  border: 0;
}

.lp-calendar-elements button span {
  display: block;
  line-height: 1.9em;
  height: 1.8em;
  border-radius: var(--elem-border-radius);
}

.lp-calendar-elements button:not(:disabled):hover {
  border: 1px solid var(--primary);
}

.lp-calendar-elements button.selected,
.lp-calendar-elements button.selected span {
  background-color: var(--primary);
  color: var(--white);
}

.lp-calendar-elements button {
  margin-left: 0;
  padding: 10px !important;
  border: 1px solid transparent;
}

.lp-calendar-elements button:disabled {
  background: transparent !important;
  color: var(--black-30);
}

.lp-calendar-popout .lp-btn--neutral {
  box-shadow: none;
}

.lp .datepicker-notifications {
  position: absolute;
  top: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 0.2rem;

  .notification-dot-sm {
    height: 0.5rem;
    width: 0.5rem;
    position: relative;
  }
}

.lp-highlight-datepicker-tags {
  text-align: left;

  .tag {
    margin: 0 5px 5px 0;
  }
}
</style>
